// src/pages/AutomationV2/hooks/useRuleWizard.ts
import * as React from 'react';

import { automationApi } from '../api/automationApi';
import { useRuleStore } from '../state/ruleStore';

import type { ConditionGroup, ConditionLeaf, ConditionNode, Operator } from '../types/rule';

export function useRuleWizard() {
  const {
    draft,
    setGeneral,
    selectedTrigger,
    previousTriggerUuid,
    setPreviousTriggerUuid,
    existingTriggerUuid,
    setExistingTriggerUuid,
    setConditionsRoot,
    setLoading, // NOVO: Controle de loading
  } = useRuleStore();

  // Helper para buscar condition_def_id baseado no left_operand
  const getConditionDefId = (leftOperand: string): number | undefined => {
    console.log('🔍 getConditionDefId - leftOperand:', leftOperand);
    console.log('🔍 selectedTrigger:', selectedTrigger);
    console.log('🔍 condition_definitions:', selectedTrigger?.condition_definitions);

    if (!selectedTrigger?.condition_definitions) {
      console.log('⚠️ Sem condition_definitions no trigger');
      return undefined;
    }

    const condDef = selectedTrigger.condition_definitions.find(cd => cd.key === leftOperand);
    console.log('🔍 condition_definition encontrada:', condDef);

    // ✅ IMPORTANTE: Só retornar ID se existir e for válido
    // Se não tiver ID, retornar undefined (backend vai aceitar null/undefined)
    const defId = condDef?.id;
    console.log('🔍 condition_def_id:', defId ?? 'undefined (será omitido no payload)');

    return defId;
  };

  // -----------------------------
  // GERAL
  // -----------------------------

  const saveGeneral = React.useCallback(
    async ({
      createdBy,
      organizationUuid,
    }: {
      createdBy: string;
      organizationUuid: string;
    }): Promise<string> => {
      try {
        setLoading({
          isLoadingGeneral: true,
          currentOperation: draft.uuid ? 'Atualizando informações da regra...' : 'Criando regra...',
        });

        let ruleUuid: string;
        let ruleId: number;

        if (!draft.uuid) {
          // Criar regra
          console.log('🔍 Criando nova regra...');
          const res = await automationApi.createRule({
            name: draft.name,
            description: draft.description,
            is_active: draft.isActive,
            created_by: createdBy,
            organization_uuid: organizationUuid,
          });
          setGeneral({
            id: res.id,
            uuid: res.uuid,
            created_by: createdBy,
            organization_uuid: organizationUuid,
          });
          ruleUuid = res.uuid;
          ruleId = res.id;
          console.log('✅ Regra criada:', { id: ruleId, uuid: ruleUuid });
        } else {
          // Atualizar regra
          console.log('🔍 Atualizando regra existente...');
          await automationApi.patchRule(draft.uuid, {
            name: draft.name,
            description: draft.description,
            is_active: draft.isActive,
          });
          ruleUuid = draft.uuid;
          ruleId = draft.id!;
          console.log('✅ Regra atualizada:', { id: ruleId, uuid: ruleUuid });
        }

        // Gerenciar trigger (criar/atualizar/deletar)
        if (selectedTrigger) {
          console.log('🔍 Gerenciando trigger para a regra...');
          console.log('🔍 selectedTrigger:', selectedTrigger);
          console.log('🔍 previousTriggerUuid:', previousTriggerUuid);
          console.log('🔍 existingTriggerUuid:', existingTriggerUuid);

          try {
            // Importar dinamicamente para evitar dependência circular
            const { triggersApi } = await import('../api/triggersApi');

            // ✅ SEMPRE deletar TODOS os triggers existentes (evitar duplicação)
            console.log('🗑️ Buscando regra para deletar TODOS os triggers existentes...');
            const ruleDto = await automationApi.getRule(ruleUuid);

            if (ruleDto.triggers && ruleDto.triggers.length > 0) {
              console.log(`Encontrados ${ruleDto.triggers.length} triggers - deletando TODOS...`);

              const deletePromises = ruleDto.triggers.map(async (trigger: any) => {
                try {
                  console.log('🗑️ Deletando trigger:', trigger.uuid);
                  await triggersApi.deleteTrigger(trigger.uuid);
                } catch (error: any) {
                  if (error.response?.status === 404) {
                    console.log('⚠️ Trigger já foi deletado (404)');
                  } else {
                    console.error('❌ Erro ao deletar trigger:', error);
                  }
                }
              });

              await Promise.allSettled(deletePromises);
              console.log('✅ Todos os triggers deletados');
            }

            // Verificar se o trigger mudou (para deletar condições)
            const triggerChanged =
              previousTriggerUuid && previousTriggerUuid !== selectedTrigger.uuid;

            if (triggerChanged) {
              console.log('⚠️ Trigger mudou! Deletando condições...');
              await automationApi.deleteAllConditions(ruleUuid);
              setConditionsRoot(undefined);
              console.log('✅ Condições deletadas');
            }

            // ✅ Criar APENAS 1 trigger novo
            const triggerData = {
              rule_id: ruleId,
              trigger_def_id: selectedTrigger.id,
              trigger_config: {}, // Por enquanto vazio, pode ser expandido no futuro
            };

            console.log('🔍 Criando trigger:', triggerData);
            const createdTrigger = await triggersApi.createTrigger(triggerData);
            console.log('✅ Trigger criado com sucesso:', createdTrigger);

            // Salvar UUID do trigger criado e atualizar previous
            setExistingTriggerUuid(createdTrigger.uuid);
            setPreviousTriggerUuid(selectedTrigger.uuid);
          } catch (error) {
            console.error('❌ Erro ao gerenciar trigger:', error);
            throw error;
          }
        } else {
          console.log('⚠️ Nenhum trigger selecionado');
        }

        return ruleUuid;
      } catch (error) {
        console.error('❌ Erro em saveGeneral:', error);
        throw error;
      } finally {
        setLoading({ isLoadingGeneral: false, currentOperation: null });
      }
    },
    [
      draft.name,
      draft.description,
      draft.isActive,
      draft.uuid,
      draft.id,
      selectedTrigger,
      previousTriggerUuid,
      existingTriggerUuid,
      setGeneral,
      setPreviousTriggerUuid,
      setExistingTriggerUuid,
      setConditionsRoot,
      setLoading,
    ]
  );

  // -----------------------------
  // CONDIÇÕES
  // -----------------------------
  const saveConditions = React.useCallback(async () => {
    if (!draft.uuid || !draft.id || !draft.conditionsRoot) return;

    try {
      setLoading({ isLoadingConditions: true, currentOperation: 'Salvando condições...' });

      // Deletar todas as condições existentes
      await automationApi.deleteAllConditions(draft.uuid);

      const root = draft.conditionsRoot as ConditionGroup;

      // ✅ PASSO 1: Aplicar agrupamento hierárquico 2 a 2
      // Se temos apenas folhas (ConditionLeaf) no children do root, aplicar buildConditionHierarchy
      const { buildConditionHierarchy } = await import('../utils/conditionHierarchy');

      console.log('🏗️ === SALVANDO CONDIÇÕES ===');
      console.log('📥 Condições originais (root.children):', root.children);

      let hierarchicalRoot: ConditionGroup;

      // Verificar se root.children contém apenas folhas (ConditionLeaf)
      const hasOnlyLeaves = root.children.every(child => child.type === 'rule');

      if (hasOnlyLeaves && root.children.length > 0) {
        console.log('✅ Aplicando agrupamento hierárquico 2 a 2...');
        // Converter lista flat de folhas em hierarquia
        hierarchicalRoot = buildConditionHierarchy(root.children as ConditionLeaf[]);
        console.log('🎯 Hierarquia gerada:', hierarchicalRoot);
      } else {
        console.log('⏭️ Root já é hierárquico ou vazio, usando como está');
        hierarchicalRoot = root;
      }

      // ✅ PASSO 2: Criar o grupo raiz no backend
      const createdRoot = await automationApi.createCondition({
        rule_id: draft.id,
        parent_id: null,
        is_group: true,
        logical_operator: hierarchicalRoot.logical || root.logical,
        operator: null,
        left_operand: null,
        right_operand: null,
        order: 0,
        condition_def_id: undefined, // Grupos não têm condition_definition
        extras: {}, // Grupos não precisam de extras
      });

      console.log('Grupo raiz criado:', createdRoot);

      // PASSO 3: Criar os filhos recursivamente (agora já hierarquizados)
      await createChildrenRecursively(draft.id, hierarchicalRoot.children, createdRoot.id);

      console.log('🏁 === FIM SALVAMENTO CONDIÇÕES ===');
    } catch (error) {
      console.error('❌ Erro ao salvar condições:', error);
      throw error;
    } finally {
      setLoading({ isLoadingConditions: false, currentOperation: null });
    }
  }, [draft.uuid, draft.id, draft.conditionsRoot, setLoading]);

  // Cria filhos recursivamente (DFS) - versão melhorada
  const createChildrenRecursively = async (
    ruleId: number,
    children: ConditionNode[],
    parentServerId: number
  ) => {
    for (let order = 0; order < children.length; order++) {
      const node = children[order];

      if (node.type === 'group') {
        const group = node as ConditionGroup;
        const createdGroup = await automationApi.createCondition({
          rule_id: ruleId,
          parent_id: parentServerId,
          is_group: true,
          logical_operator: group.logical,
          operator: null,
          left_operand: null,
          right_operand: null,
          order,
          condition_def_id: undefined, // Grupos não têm condition_definition
          extras: {}, // Grupos não precisam de extras
        });

        // Recursivamente criar os filhos do grupo
        await createChildrenRecursively(ruleId, group.children, createdGroup.id);
      } else {
        const leaf = node as ConditionLeaf;

        // Normalizar o valor baseado no operador
        const rightOperand = normalizeRightOperand(leaf.operator, leaf.right);

        // Construir extras baseado no meta (para form_submission)
        const extras: Record<string, any> = {};

        if (leaf.meta) {
          if (leaf.meta.form_uuid) {
            extras.form_uuid = leaf.meta.form_uuid;
          }
          if (leaf.meta.field_uuid) {
            extras.field_uuid = leaf.meta.field_uuid;
          }
          if (leaf.meta.field_label) {
            extras.field_label = leaf.meta.field_label;
          }
          // ✅ CRÍTICO: Salvar field_type para otimizar hidratação futura!
          if (leaf.meta.field_type) {
            extras.field_type = leaf.meta.field_type;
          }

          // Se for submission.option_id, incluir option_id no extras
          if (leaf.left === 'submission.option_id' && leaf.right) {
            // Para operadores IN/NOT_IN, right é array
            if (Array.isArray(rightOperand)) {
              extras.option_ids = rightOperand;
            } else {
              extras.option_id = rightOperand;
            }
          }

          // ✅ Se tiver valor de "Outro", incluir
          if (leaf.meta.outro_value) {
            extras.outro_value = leaf.meta.outro_value;
          }

          // ✅ NOVO: Para submission.value, incluir option_value (tags, textarea)
          if (leaf.left === 'submission.value' && leaf.meta.option_value) {
            extras.option_value = leaf.meta.option_value;
          }

          // ✅ NOVO: Para geo_state, usar meta.geo_state e meta.geo_city
          if (leaf.meta.geo_state && leaf.meta.geo_city) {
            extras.state = leaf.meta.geo_state;
            extras.city = leaf.meta.geo_city;
            console.log('🌍 Salvando extras geo:', { state: extras.state, city: extras.city });
          }
          // Fallback: Se for geo (right tem formato "Estado | Cidade"), parsear e salvar
          else if (
            leaf.left === 'submission.value' &&
            typeof rightOperand === 'string' &&
            rightOperand.includes(' | ')
          ) {
            const [state, city] = rightOperand.split(' | ');
            extras.state = state;
            extras.city = city;
            console.log('🌍 Salvando extras geo (fallback):', { state, city });
          }
        }

        // ✅ Buscar condition_def_id baseado no left_operand
        const conditionDefId = getConditionDefId(leaf.left);

        console.log('🔍 Criando condição leaf:', {
          left: leaf.left,
          operator: leaf.operator,
          right: rightOperand,
          meta: leaf.meta,
          extras,
          condition_def_id: conditionDefId,
        });

        await automationApi.createCondition({
          rule_id: ruleId,
          parent_id: parentServerId,
          is_group: false,
          logical_operator: null,
          operator: leaf.operator as Operator,
          left_operand: leaf.left,
          right_operand: rightOperand,
          order,
          condition_def_id: conditionDefId, // ✅ ID da condition_definition
          extras, // ✅ Enviar extras com form_uuid, field_uuid, option_id
        });
      }
    }
  };

  // Função auxiliar para normalizar o operando direito
  const normalizeRightOperand = (
    operator: Operator,
    right: string | string[]
  ): string | string[] => {
    if (operator === 'IN' || operator === 'NOT_IN') {
      // Para operadores IN/NOT_IN, sempre retornar array
      return Array.isArray(right) ? right : right ? [String(right)] : [];
    }

    // Para outros operadores, sempre retornar string
    if (Array.isArray(right)) {
      return right[0] ?? '';
    }

    return right ?? '';
  };

  // -----------------------------
  // AÇÕES
  // -----------------------------
  const saveActions = React.useCallback(async () => {
    console.log('🔍 === INÍCIO saveActions ===');
    console.log('🔍 Draft UUID:', draft.uuid);
    console.log('🔍 Draft ID:', draft.id);
    console.log('🔍 Draft Actions:', draft.actions);
    console.log('🔍 Actions length:', draft.actions?.length);

    if (!draft.uuid || !draft.id) {
      console.log('⚠️ Sem UUID ou ID da regra - ABORTANDO saveActions');
      return;
    }

    if (!draft.actions || draft.actions.length === 0) {
      console.log('ℹ️ Nenhuma ação para salvar - ABORTANDO saveActions');
      return;
    }

    try {
      setLoading({ isLoadingActions: true, currentOperation: 'Salvando ações...' });

      // Importar dinamicamente
      const { actionsApi } = await import('../api/actionsApi');
      const { convertToApiActions } = await import('../utils/actionUtils');

      // ✅ RECONCILIAÇÃO: Buscar ações existentes no backend
      console.log('🔍 Buscando ações existentes da regra...');
      const existingActions = await actionsApi.getActionsByRule(draft.uuid);
      console.log(`📊 Ações existentes no backend: ${existingActions.length}`, existingActions);
      console.log(`📊 Ações no draft (UI): ${draft.actions?.length || 0}`, draft.actions);

      // Criar Set com UUIDs das ações no draft (que têm UUID = já existem)
      const draftActionUuids = new Set(
        (draft.actions || [])
          .filter(a => a.id) // Ações com id são as que já existiam
          .map(a => a.id)
      );

      console.log('🔍 UUIDs de ações mantidas no draft:', Array.from(draftActionUuids));

      // ✅ DELETAR ações que não estão mais no draft
      const actionsToDelete = existingActions.filter(
        existing => !draftActionUuids.has(existing.uuid)
      );

      if (actionsToDelete.length > 0) {
        console.log(`🗑️ Deletando ${actionsToDelete.length} ações removidas...`);

        const deletePromises = actionsToDelete.map(async action => {
          try {
            console.log('🗑️ Deletando ação:', action.uuid, '-', action.action_definition.name);
            await actionsApi.deleteAction(action.uuid);
          } catch (error: any) {
            if (error.response?.status === 404) {
              console.log('⚠️ Ação já foi deletada (404)');
            } else {
              console.error('❌ Erro ao deletar ação:', error);
            }
          }
        });

        await Promise.allSettled(deletePromises);
        console.log('✅ Ações removidas deletadas');
      }

      // ✅ DELETAR TODAS as ações existentes (estratégia simples)
      console.log('🗑️ Deletando TODAS as ações existentes antes de recriar...');
      await actionsApi.deleteAllActions(draft.uuid);
      console.log('✅ Todas as ações deletadas');

      // ✅ CRIAR TODAS as ações do draft (mesmo as que já existiam)
      if (draft.actions && draft.actions.length > 0) {
        console.log(`➕ Criando ${draft.actions.length} ações...`);

        const apiActions = convertToApiActions(draft.actions, draft.id.toString());
        console.log('🔍 Ações convertidas para API:', apiActions);

        for (let i = 0; i < apiActions.length; i++) {
          const apiAction = apiActions[i];
          console.log(`🔍 Criando ação ${i + 1}/${apiActions.length}:`, apiAction);
          console.log(`   - config:`, apiAction.config);

          const createdAction = await actionsApi.createAction(apiAction);
          console.log(`✅ Ação ${i + 1} criada com sucesso:`, createdAction);
        }

        console.log('✅ Todas as ações criadas com sucesso!');
      } else {
        console.log('ℹ️ Nenhuma ação para criar');
      }

      console.log('✅ Reconciliação de ações completa');
    } catch (error) {
      console.error('❌ Erro ao salvar ações:', error);
      throw error;
    } finally {
      setLoading({ isLoadingActions: false, currentOperation: null });
    }
  }, [draft.uuid, draft.id, draft.actions, setLoading]);

  // -----------------------------
  // FINALIZAR
  // -----------------------------
  const finalize = React.useCallback(async () => {
    // caso haja algo que precise rodar no final, coloque aqui.
  }, []);

  return {
    saveGeneral,
    saveConditions,
    saveActions,
    finalize,
  };
}
